/**
 * Property types for listings — one vocabulary, four categories.
 *
 * `property_listings.property_type` is a free varchar, and a sale raised from a
 * listing copies it verbatim (SaleFormPage / SaleCreate on create, the
 * sync_listing_to_sales trigger after). Until Sep 2026 the listing forms offered
 * eight residential labels and nothing else, so a factory or a plot of land could
 * only be filed as "Other" — the flaw Andy raised on 04-09.
 *
 * The category is NOT stored. It is derived from the type and does two things on
 * the form: it narrows the type picker, and it decides which detail fields apply
 * (a plot of land has no bedrooms, a factory no furnishing).
 *
 * Values are the labels: what is stored is what every list card, detail page and
 * sale property line prints. The residential values are the pre-existing ones,
 * unchanged, so nothing already saved moves. The commercial, industrial and land
 * values follow the vocabulary the migrated erp213 sales already carry ("Shop
 * Office", "Link Factory", "Industrial Land", …), so a listing-born sale and a
 * migrated one read alike.
 *
 * Kept in lockstep with cid_admin_portal/src/utils/propertyTypes.js.
 */

export const PROPERTY_CATEGORIES = [
  {
    key: 'RESIDENTIAL',
    label: 'Residential',
    types: [
      'Condominium', 'Serviced Residence', 'Apartment', 'Flat', 'SOHO',
      'Terrace', 'Cluster House', 'Townhouse', 'Semi-D', 'Bungalow', 'Villa', 'Other',
    ],
  },
  {
    key: 'COMMERCIAL',
    label: 'Commercial',
    types: [
      'Shop', 'Shop Office', 'Retail Space', 'Office', 'SOVO',
      'Commercial Bungalow', 'Commercial Semi-D', 'Hotel / Resort', 'Other Commercial',
    ],
  },
  {
    key: 'INDUSTRIAL',
    label: 'Industrial',
    types: [
      'Factory', 'Link Factory', 'Semi-D Factory', 'Detached Factory', 'Cluster Factory',
      'Warehouse', 'Light Industrial', 'Other Industrial',
    ],
  },
  {
    key: 'LAND',
    label: 'Land',
    types: [
      'Residential Land', 'Commercial Land', 'Industrial Land', 'Agricultural Land',
      'Bungalow Land', 'Other Land',
    ],
  },
];

export const DEFAULT_CATEGORY = 'RESIDENTIAL';

export const CATEGORY_OPTIONS = PROPERTY_CATEGORIES.map((c) => ({ value: c.key, label: c.label }));

// Detail fields a category has no use for. The form hides them and the save sends
// them as null, so a listing re-filed from Residential to Land does not keep its
// three bedrooms. Land area and tenure apply to everything.
const HIDDEN_FIELDS = {
  RESIDENTIAL: [],
  COMMERCIAL: ['bedrooms'],
  INDUSTRIAL: ['bedrooms', 'furnish', 'floor_level'],
  LAND: ['bedrooms', 'bathrooms', 'car_parks', 'built_up_sqft', 'furnish', 'floor_level', 'facing'],
};

const TYPE_INDEX = new Map();
PROPERTY_CATEGORIES.forEach((c) => c.types.forEach((t) => TYPE_INDEX.set(t.toLowerCase(), { type: t, category: c.key })));

/** The vocabulary spelling of a stored value ("CONDOMINIUM" → "Condominium"), or the value as-is when unknown. */
export function canonicalType(type) {
  if (type == null || type === '') return type;
  const hit = TYPE_INDEX.get(String(type).trim().toLowerCase());
  return hit ? hit.type : type;
}

/** Category key for a type, or null when the value is not in the vocabulary. */
export function categoryOfType(type) {
  if (type == null || type === '') return null;
  const hit = TYPE_INDEX.get(String(type).trim().toLowerCase());
  return hit ? hit.category : null;
}

/**
 * Picker options for a category. A stored value outside the vocabulary (an old
 * row, or one the assistant created from free text) is appended so the field
 * still shows it instead of silently blanking on the next save.
 */
export function typeOptions(categoryKey, current) {
  const cat = PROPERTY_CATEGORIES.find((c) => c.key === categoryKey) || PROPERTY_CATEGORIES[0];
  const opts = cat.types.map((t) => ({ value: t, label: t }));
  if (current && !opts.some((o) => o.value === current)) opts.push({ value: current, label: current });
  return opts;
}

export function showsField(categoryKey, field) {
  return !(HIDDEN_FIELDS[categoryKey] || []).includes(field);
}

/** Copy of `payload` with the category's hidden fields set to null (an explicit null reaches the server; an omitted key does not). */
export function clearHiddenFields(payload, categoryKey) {
  const out = { ...payload };
  (HIDDEN_FIELDS[categoryKey] || []).forEach((k) => { out[k] = null; });
  return out;
}

const SQFT_PER_ACRE = 43560;
const SQFT_PER_HECTARE = 107639.104;

/** "≈ 2.50 acres (1.01 ha)" for a land area in sqft, or null when there is nothing to convert. */
export function landAreaWords(sqft) {
  const n = Number(sqft);
  if (!(n > 0)) return null;
  const acres = n / SQFT_PER_ACRE;
  const ha = n / SQFT_PER_HECTARE;
  const fmt = (v) => (v >= 100 ? v.toFixed(0) : v.toFixed(2));
  return `≈ ${fmt(acres)} acres (${fmt(ha)} ha)`;
}
